const FUEL_SECTORS_KEY = 'table:fuel-consumption-sectors';
const OIL_SECTORS_KEY = 'table:oil-consumption-sectors';

export const SECTOR_USAGE_SECTION_KEYS = new Set([FUEL_SECTORS_KEY, OIL_SECTORS_KEY]);

export function isSectorUsageSection(sectionKey) {
  return SECTOR_USAGE_SECTION_KEYS.has(String(sectionKey || ''));
}

export function isFuelSectorUsageSection(sectionKey) {
  return String(sectionKey || '') === FUEL_SECTORS_KEY;
}

export function getSectorUsageValueKey(sectionKey) {
  if (sectionKey === OIL_SECTORS_KEY) return 'totalOilConsumption';
  return 'avgFuelKgPerHour';
}

export function getSectorUsageValueLabel(sectionKey) {
  if (sectionKey === OIL_SECTORS_KEY) return 'Oil Used (ml)';
  return 'Avg Fuel (kg/hr)';
}

function parseEntryDate(value) {
  if (!value) return 0;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function buildPointLabel(row, index) {
  const datePart = row.entryDate || '';
  const aircraft = row.aircraft || '';
  const sectorNo = row.sectorNumber != null && row.sectorNumber !== ''
    ? `S${row.sectorNumber}`
    : `#${index + 1}`;
  const parts = [datePart, aircraft, sectorNo].filter(Boolean);
  return parts.join(' · ') || `Sector ${index + 1}`;
}

function buildRouteLabel(row) {
  const from = row.departurePlace || '';
  const to = row.arrivalPlace || '';
  if (from && to) return `${from} → ${to}`;
  return from || to || '';
}

function toFiniteNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function sectorBlockHours(row) {
  const fromField = toFiniteNumber(row.blockHours);
  if (fromField > 0) return fromField;
  const avg = toFiniteNumber(row.avgFuelKgPerHour);
  const fuel = toFiniteNumber(row.fuelConsumption);
  if (avg > 0 && fuel > 0) return fuel / avg;
  return 0;
}

/**
 * Aggregate TLB sector rows by charter and compute average fuel kg/hr.
 * Unlinked sectors stay as individual points so data is not dropped.
 */
export function buildFuelCharterAvgLineData(section, limit = 80) {
  const rows = Array.isArray(section?.rows) ? section.rows : [];
  const byKey = new Map();

  rows.forEach((row, index) => {
    const fuel = toFiniteNumber(row.fuelConsumption);
    const hours = sectorBlockHours(row);
    const charterNumber = String(row.charterNumber || '').trim();
    const linkedSectorId = row.linkedSectorId ? String(row.linkedSectorId) : '';
    const groupKey = charterNumber
      ? `charter:${charterNumber}`
      : `sector:${linkedSectorId || buildPointLabel(row, index)}`;

    if (!byKey.has(groupKey)) {
      byKey.set(groupKey, {
        name: charterNumber || buildPointLabel(row, index),
        charterNumber,
        totalFuelKg: 0,
        totalBlockHours: 0,
        sectorCount: 0,
        linkedSectorId,
        tripId: row.tripId ? String(row.tripId) : '',
        entryDate: row.entryDate || '',
        aircraft: row.aircraft || '',
        sectorNumber: charterNumber ? '' : (row.sectorNumber ?? ''),
        route: charterNumber ? '' : buildRouteLabel(row),
        sheetNumber: row.sheetNumber || '',
        sortDate: parseEntryDate(row.entryDate),
        sortName: charterNumber || buildPointLabel(row, index)
      });
    }

    const agg = byKey.get(groupKey);
    agg.totalFuelKg += fuel;
    agg.totalBlockHours += hours;
    agg.sectorCount += 1;
    if (!agg.linkedSectorId && linkedSectorId) agg.linkedSectorId = linkedSectorId;
    if (!agg.tripId && row.tripId) agg.tripId = String(row.tripId);
    const entrySort = parseEntryDate(row.entryDate);
    if (entrySort && (entrySort < agg.sortDate || !agg.sortDate)) {
      agg.sortDate = entrySort;
      agg.entryDate = row.entryDate || agg.entryDate;
    }
    if (charterNumber) {
      const aircraftSet = new Set(
        String(agg.aircraft || '')
          .split(', ')
          .map((part) => part.trim())
          .filter(Boolean)
      );
      if (row.aircraft) aircraftSet.add(String(row.aircraft));
      agg.aircraft = [...aircraftSet].join(', ');
    }
  });

  const points = Array.from(byKey.values())
    .map((agg) => {
      const value = agg.totalBlockHours > 0
        ? Number((agg.totalFuelKg / agg.totalBlockHours).toFixed(2))
        : 0;
      return {
        name: agg.name,
        value,
        linkedSectorId: agg.linkedSectorId,
        tripId: agg.tripId,
        entryDate: agg.entryDate,
        aircraft: agg.aircraft,
        sectorNumber: agg.sectorNumber,
        route: agg.route,
        sheetNumber: agg.sheetNumber,
        charterNumber: agg.charterNumber,
        sectorCount: agg.sectorCount,
        totalFuelKg: Number(agg.totalFuelKg.toFixed(1)),
        totalBlockHours: Number(agg.totalBlockHours.toFixed(2)),
        sortDate: agg.sortDate,
        sortName: agg.sortName
      };
    })
    .filter((point) => point.value > 0 || point.linkedSectorId || point.charterNumber)
    .sort((a, b) => {
      if (a.sortDate !== b.sortDate) return a.sortDate - b.sortDate;
      return String(a.sortName).localeCompare(String(b.sortName));
    });

  const truncated = points.length > limit;
  const data = truncated ? points.slice(-limit) : points;

  return {
    data,
    truncated,
    valueKey: 'avgFuelKgPerHour',
    valueLabel: getSectorUsageValueLabel(FUEL_SECTORS_KEY),
    clickableCount: data.filter((point) => point.linkedSectorId).length,
    mode: 'charter-avg'
  };
}

/**
 * Build chronological line-chart points for fuel/oil sector usage sections.
 * Fuel: average kg/hr aggregated per charter.
 * Oil: sector-wise usage (unchanged).
 * Keeps linkedSectorId so chart clicks can open /ops/sectors/:id.
 */
export function buildSectorUsageLineData(section, limit = 80) {
  if (isFuelSectorUsageSection(section?.sectionKey)) {
    return buildFuelCharterAvgLineData(section, limit);
  }

  const valueKey = getSectorUsageValueKey(section?.sectionKey);
  const rows = Array.isArray(section?.rows) ? section.rows : [];

  const points = rows
    .map((row, index) => {
      const value = Number(row[valueKey]);
      return {
        name: buildPointLabel(row, index),
        value: Number.isFinite(value) ? value : 0,
        linkedSectorId: row.linkedSectorId ? String(row.linkedSectorId) : '',
        entryDate: row.entryDate || '',
        aircraft: row.aircraft || '',
        sectorNumber: row.sectorNumber ?? '',
        route: buildRouteLabel(row),
        sheetNumber: row.sheetNumber || '',
        sortDate: parseEntryDate(row.entryDate),
        sortSector: Number(row.sectorNumber) || 0
      };
    })
    .filter((point) => point.value > 0 || point.linkedSectorId)
    .sort((a, b) => {
      if (a.sortDate !== b.sortDate) return a.sortDate - b.sortDate;
      if (a.aircraft !== b.aircraft) return String(a.aircraft).localeCompare(String(b.aircraft));
      return a.sortSector - b.sortSector;
    });

  const truncated = points.length > limit;
  const data = truncated ? points.slice(-limit) : points;

  return {
    data,
    truncated,
    valueKey,
    valueLabel: getSectorUsageValueLabel(section?.sectionKey),
    clickableCount: data.filter((point) => point.linkedSectorId).length,
    mode: 'sector'
  };
}

export function openLinkedSector(linkedSectorId) {
  const id = String(linkedSectorId || '').trim();
  if (!id) return false;
  window.open(`/ops/sectors/${id}`, '_blank', 'noopener,noreferrer');
  return true;
}
